import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useGoogleAnalytics } from '@/composables/useGoogleAnalytics'
import { getSafeAuthNavigation, protectInitialAuthLink } from '@/utils/authLinkParameters'
import type { RouteRecordRaw } from 'vue-router'

// Lazy load components for better performance
const HomePage = () => import('@/views/HomePage.vue')
const LoginPage = () => import('@/views/auth/LoginPage.vue')
const RegisterPage = () => import('@/views/auth/RegisterPage.vue')
const ForgotPasswordPage = () => import('@/views/auth/ForgotPasswordPage.vue')
const EmailVerificationPage = () => import('@/views/auth/EmailVerificationPage.vue')
const VerifyEmailResultPage = () => import('@/views/auth/VerifyEmailResultPage.vue')
const GoogleCallbackPage = () => import('@/views/auth/GoogleCallbackPage.vue')
const AccountHubPage = () => import('@/views/account/AccountHubPage.vue')
const EditProfilePage = () => import('@/views/profile/EditProfilePage.vue')
const PhotosPage = () => import('@/views/profile/PhotosPage.vue')
const GreetingPage = () => import('@/views/profile/GreetingPage.vue')
const LikedUsersPage = () => import('@/views/profile/LikedUsersPage.vue')
const FavoritesPage = () => import('@/views/relationship/FavoritesPage.vue')
const BlacklistPage = () => import('@/views/relationship/BlacklistPage.vue')
const DiscoverPage = () => import('@/views/discover/DiscoverPagePremium.vue')
const UserDetailPage = () => import('@/views/discover/UserDetailPage.vue')
const MessagesPage = () => import('@/views/messages/MessagesPage.vue')
const ChatPage = () => import('@/views/messages/ChatPage.vue')
const SubscriptionUnavailablePage = () => import('@/views/subscription/SubscriptionUnavailablePage.vue')
const SettingsPage = () => import('@/views/settings/SettingsPage.vue')
const NotificationsPage = () => import('@/views/NotificationsPage.vue')
const HelpPage = () => import('@/views/HelpPage.vue')
const PrivacyPage = () => import('@/views/legal/PrivacyPage.vue')
const TermsPage = () => import('@/views/legal/TermsPage.vue')
const NotFoundPage = () => import('@/views/NotFoundPage.vue')

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'home',
    component: HomePage,
    meta: {
      title: 'SugarM｜不限性別的成人互助交友平台',
      description: '男生可以找男生，女生可以找女生；金主與 Baby 都能選擇男性、女性，或兩者皆可。'
    }
  },
  {
    path: '/home',
    redirect: '/'
  },
  {
    path: '/login',
    redirect: (to) => ({
      path: '/auth/login',
      query: to.query,
      hash: to.hash
    })
  },
  {
    path: '/register',
    redirect: (to) => ({
      path: '/auth/register',
      query: to.query,
      hash: to.hash
    })
  },
  {
    path: '/auth',
    name: 'auth',
    redirect: '/auth/login',
    children: [
      {
        path: 'login',
        name: 'login',
        component: LoginPage,
        meta: {
          title: 'Login - SugarM',
          requiresGuest: true
        }
      },
      {
        path: 'register',
        name: 'register',
        component: RegisterPage,
        meta: {
          title: 'Join SugarM - Create Your Account',
          requiresGuest: true
        }
      },
      {
        path: 'forgot-password',
        name: 'forgot-password',
        component: ForgotPasswordPage,
        meta: {
          title: 'Reset Password - SugarM',
          requiresGuest: true
        }
      },
      {
        path: 'reset-password',
        name: 'reset-password',
        component: () => import('@/views/auth/ResetPasswordPage.vue'),
        meta: {
          title: 'Create New Password - SugarM',
          requiresGuest: true
        }
      },
      {
        path: 'verify-email',
        name: 'verify-email',
        component: EmailVerificationPage,
        meta: {
          title: 'Verify Your Email - SugarM'
        }
      },
      {
        path: 'verify-email-result',
        name: 'verify-email-result',
        component: VerifyEmailResultPage,
        meta: {
          title: 'Email Verification - SugarM'
        }
      },
      {
        path: 'google/callback',
        name: 'google-callback',
        component: GoogleCallbackPage,
        meta: { title: 'Completing Google Sign-in' }
      }
    ]
  },
  {
    path: '/account',
    name: 'account',
    redirect: '/profile',
    meta: {
      title: 'My Account - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/profile',
    name: 'profile',
    component: AccountHubPage,
    meta: {
      title: 'My Profile - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/profile/edit',
    name: 'edit-profile',
    component: EditProfilePage,
    meta: {
      title: 'Edit Profile - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/profile/photos',
    name: 'photos',
    component: PhotosPage,
    meta: {
      title: 'Manage Photos - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/profile/greeting',
    name: 'greeting',
    component: GreetingPage,
    meta: {
      title: 'Edit My Greeting - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/liked-users',
    name: 'liked-users',
    component: LikedUsersPage,
    meta: {
      title: 'Liked Users - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/favorites',
    name: 'favorites',
    component: FavoritesPage,
    meta: {
      title: 'My Favorites - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/blacklist',
    name: 'blacklist',
    component: BlacklistPage,
    meta: {
      title: 'Blocked Users - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/discover',
    name: 'discover',
    component: DiscoverPage,
    meta: {
      title: 'Discover Premium Members - SugarM',
      requiresAuth: false // Allow guest browsing
    }
  },
  // {
  //   path: '/debug/auth',
  //   name: 'debug-auth',
  //   component: () => import('@/views/DebugAuth.vue'),
  //   meta: {
  //     title: 'Auth Debug - SugarM'
  //   }
  // },

  // {
  //   path: '/user/demo',
  //   name: 'user-profile-demo',
  //   component: () => import('@/views/discover/UserProfileDemo.vue'),
  //   meta: {
  //     title: 'User Profile Translation Demo - SugarM'
  //   }
  // },
  {
    path: '/wish-tree',
    name: 'wish-tree',
    component: () => import('@/views/wish/WishTreePage.vue'),
    meta: {
      title: 'Wish Tree - SugarM'
    }
  },
  {
    path: '/user/:id',
    name: 'user-detail',
    component: UserDetailPage,
    meta: {
      title: 'Member Profile - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/messages',
    name: 'messages',
    component: MessagesPage,
    meta: {
      title: 'Messages - SugarM',
      requiresAuth: true
      // 移除 requiresVip，讓組件內部處理權限檢查
    }
  },
  {
    path: '/chat/:userId',
    name: 'chat',
    component: ChatPage,
    meta: {
      title: 'Chat - SugarM',
      requiresAuth: true,
      requiresVipCheck: true // 需要在組件內檢查 VIP 權限
    },
    beforeEnter: (to, from, next) => {
      // 驗證 userId 參數是否為有效的數字
      const userId = to.params.userId as string
      if (!/^\d+$/.test(userId)) {
        console.log('❌ 無效的用戶 ID:', userId)
        next('/messages')
        return
      }

      // 檢查是否是從合法路由進入（防止直接 URL 訪問）
      const validFromRoutes = ['/messages', '/user/', '/explore', '/']
      const isValidNavigation = from.path === '/' ||
                               validFromRoutes.some(route => from.path.startsWith(route)) ||
                               from.name === 'user-detail'

      if (!isValidNavigation && from.path !== '/') {
        console.log('❌ 非法的聊天頁面訪問，來源:', from.path)
        next('/messages')
        return
      }

      next()
    }
  },
  {
    path: '/subscription',
    name: 'subscription',
    component: SubscriptionUnavailablePage,
    meta: {
      title: 'Membership Not Available - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/subscription/:pathMatch(.*)*',
    redirect: '/subscription'
  },
  {
    path: '/settings',
    name: 'settings',
    component: SettingsPage,
    meta: {
      title: 'Settings - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/notifications',
    name: 'notifications',
    component: NotificationsPage,
    meta: {
      title: 'Notifications - SugarM',
      requiresAuth: true
    }
  },
  {
    path: '/help',
    name: 'help',
    component: HelpPage,
    meta: {
      title: 'SugarM 幫助中心｜糖寶貝免費使用',
      description: 'SugarM 幫助中心提供個人檔案、配對、訊息、安全與會員使用說明；糖寶貝可免費使用 SugarM。'
    }
  },
  {
    path: '/feedback',
    name: 'feedback',
    component: () => import('@/views/support/FeedbackPage.vue'),
    meta: {
      title: 'Feedback & Report - SugarM'
    }
  },
  {
    path: '/privacy',
    name: 'privacy',
    component: PrivacyPage,
    meta: {
      title: 'Privacy Policy - SugarM'
    }
  },
  {
    path: '/terms',
    name: 'terms',
    component: TermsPage,
    meta: {
      title: 'Terms of Service - SugarM'
    }
  },
  // Debug page (commented out - file not found)
  // {
  //   path: '/debug/auth',
  //   name: 'auth-debug',
  //   component: () => import('@/views/DebugAuth.vue'),
  //   meta: {
  //     title: 'Auth Debug - SugarM'
  //   }
  // },

  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundPage,
    meta: {
      title: 'Page Not Found - SugarM'
    }
  }
]

protectInitialAuthLink()

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const safeAuthNavigation = getSafeAuthNavigation(to)
  if (safeAuthNavigation) {
    next(safeAuthNavigation)
    return
  }

  const authStore = useAuthStore()

  // Reduced logging for better UX
  if (import.meta.env.DEV) {
    console.log('Router:', to.path, 'auth:', authStore.isAuthenticated)
  }

  // Prevent infinite loops
  if (to.path === from.path && to.name === from.name) {
    next()
    return
  }

  // Allow normal navigation between login and discover pages
  // Removed loop prevention as discover is now the default post-login destination

  // Wait for auth store to be initialized with timeout
  if (!authStore.isInitialized) {
    try {
      await Promise.race([
        authStore.initializeAuth(),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Auth initialization timeout')), 10000)
        )
      ])
    } catch (error) {
      console.error('❌ Auth initialization failed:', error)
      authStore.isInitialized = true
    }
  }

  // Set page title
  if (to.meta.title) {
    document.title = to.meta.title as string
  }

  // Set meta description
  if (to.meta.description) {
    const metaDescription = document.querySelector('meta[name="description"]')
    if (metaDescription) {
      metaDescription.setAttribute('content', to.meta.description as string)
    }
  }

  // Check if route requires authentication
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    next({
      name: 'login',
      query: { redirect: to.fullPath }
    })
    return
  }

  // The marketing page is only for visitors. Members start from discovery.
  if (to.name === 'home' && authStore.isAuthenticated) {
    next({ name: 'discover' })
    return
  }

  // Check if route requires guest (redirect authenticated users)
  // Only redirect if we have BOTH token AND user data
  if (to.meta.requiresGuest && authStore.token && authStore.user && authStore.isAuthenticated) {
    // Prevent redirect loop: if coming from discover, don't redirect back
    if (from.name === 'discover') {
      next()
      return
    }

    // Check if there's a redirect query parameter
    const redirectTo = to.query.redirect as string
    if (redirectTo && redirectTo !== to.fullPath) {
      next(redirectTo)
    } else {
      next({ name: 'discover' })
    }
    return
  }

  // Check if route requires VIP membership
  // Note: VIP checks are now handled by individual components with better UX
  if (to.meta.requiresVip && authStore.isAuthenticated && !authStore.isVip) {
    // Allow navigation to continue, component will handle VIP check with modal
  }

  next()
})

// Analytics tracking for page views
router.afterEach((to, _from, failure) => {
  if (failure) return

  const { trackPageView } = useGoogleAnalytics()

  // Track page view with route metadata
  trackPageView({
    page_title: typeof to.meta.title === 'string' ? to.meta.title : document.title,
    page_path: to.path,
    page_location: new URL(to.path, window.location.origin).href
  })
})

export default router
